/**
 * Tarneeb — server-authoritative rules engine (Levantine partnership trick-taking, 4 players).
 *
 * Rules implemented (toggles live in {@link TarneebConfig}):
 *  - 4 players in two partnerships: seats 0 & 2 (team 0) against seats 1 & 3 (team 1). One 52-card deck, 13 each.
 *  - One round of bidding, starting left of the dealer: each player either passes or bids higher than the
 *    previous bid (7..13 tricks). The dealer may not pass when everyone else did — they must bid the minimum.
 *  - The highest bidder names the trump suit ("tarneeb") and leads the first trick; the trick winner leads next.
 *  - Players must follow the led suit when able; a void player may play anything (trump or not).
 *  - Trick winner: highest trump, else highest card of the led suit.
 *  - Scoring per round: the bidding team that makes its bid scores the tricks it won (a made bid of 13 scores
 *    `bid13Score`); if it falls short it loses the bid. The defending team always scores the tricks it won.
 *  - The match ends when a team reaches `targetScore` (and leads) or after `maxRounds`.
 *    Partners share a place, so payouts are split between the two partners.
 */
import { buildDeck, parseCard, removeCards, sortHand, SUITS, type CardId, type Suit } from '../../core/cards.js';
import { checkBool, checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface TarneebConfig {
  /** Match ends when a team reaches this score and leads. */
  targetScore: number;
  /** Hard cap on rounds so a match always terminates. */
  maxRounds: number;
  minBid: number;
  /** Points for a made bid of 13. */
  bid13Score: number;
  /** When everyone else passed, the dealer must bid. */
  dealerMustBid: boolean;
  roundEndDelayMs: number;
  trickEndDelayMs: number;
}

export type TarneebPhase = 'BIDDING' | 'TRUMP' | 'PLAYING' | 'TRICK_END' | 'ROUND_END' | 'MATCH_END';

export interface TarneebTrickPlay {
  seat: number;
  card: CardId;
}

export interface TarneebCompletedTrick {
  leader: number;
  plays: TarneebTrickPlay[];
  winner: number;
}

/** Per-round result for one team (public once the round ends). */
export interface TarneebTeamRound {
  /** True for the team that won the bidding. */
  declarer: boolean;
  /** The winning bid (same value shown on both rows). */
  bid: number;
  tricks: number;
  /** Net score change for the team this round. */
  delta: number;
}

export interface TarneebState {
  config: TarneebConfig;
  players: string[];
  round: number;
  dealer: number;
  phase: TarneebPhase;
  hands: CardId[][];
  /** Per seat: null = has not acted yet, 0 = passed, n = bid. */
  bids: (number | null)[];
  highBid: { seat: number; value: number } | null;
  trump: Suit | null;
  tricksWon: number[];
  turn: number;
  trick: TarneebTrickPlay[];
  tricks: TarneebCompletedTrick[];
  teamScores: number[];
  roundResults: TarneebTeamRound[][];
}

export type TarneebAction =
  | { type: 'bid'; value: number }
  | { type: 'pass' }
  | { type: 'trump'; suit: Suit }
  | { type: 'play'; card: CardId };

export type TarneebLegal =
  | { kind: 'bid'; min: number; max: number; canPass: boolean }
  | { kind: 'trump'; suits: Suit[] }
  | { kind: 'play'; cards: CardId[] }
  | { kind: 'none' };

export interface TarneebView {
  gameType: 'TARNEEB';
  config: TarneebConfig;
  seat: number | null;
  phase: TarneebPhase;
  round: number;
  dealer: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  bids: (number | null)[];
  highBid: { seat: number; value: number } | null;
  /** Seat that won the bidding (set once bidding is over). */
  declarer: number | null;
  trump: Suit | null;
  tricksWon: number[];
  trick: TarneebTrickPlay[];
  lastTrick: TarneebCompletedTrick | null;
  tricks: TarneebCompletedTrick[];
  teamScores: number[];
  roundResults: TarneebTeamRound[][];
  /** Score of each seat's team (so generic score UIs work). */
  totals: number[];
}

const DEFAULTS: TarneebConfig = {
  targetScore: 41,
  maxRounds: 15,
  minBid: 7,
  bid13Score: 16,
  dealerMustBid: true,
  roundEndDelayMs: 5000,
  trickEndDelayMs: 900,
};

const SEATS = 4;

export const tarneebTeamOf = (seat: number): number => seat % 2;
export const tarneebPartnerOf = (seat: number): number => (seat + 2) % SEATS;

export function tarneebTrickWinner(plays: readonly TarneebTrickPlay[], trump: Suit): number {
  const lead = parseCard(plays[0]!.card).suit;
  let best = plays[0]!;
  for (const p of plays.slice(1)) {
    const c = parseCard(p.card);
    const b = parseCard(best.card);
    const beats = (c.suit === trump && b.suit !== trump) || (c.suit === b.suit && c.rank > b.rank);
    if (beats && (c.suit === trump || c.suit === lead)) best = p;
  }
  return best.seat;
}

export function tarneebLegalCards(hand: readonly CardId[], trick: readonly TarneebTrickPlay[]): CardId[] {
  if (trick.length === 0) return [...hand];
  const lead = parseCard(trick[0]!.card).suit;
  const followers = hand.filter((c) => parseCard(c).suit === lead);
  return followers.length > 0 ? followers : [...hand];
}

/** The bid range open to the seat to act (after `highBid`). `max < min` means only passing is possible. */
export function tarneebBidRange(state: Pick<TarneebState, 'config' | 'highBid' | 'bids' | 'dealer'>, seat: number): { min: number; max: number; canPass: boolean } {
  const min = Math.max(state.config.minBid, (state.highBid?.value ?? 0) + 1);
  const forced = state.config.dealerMustBid && seat === state.dealer && state.highBid === null;
  return { min, max: 13, canPass: !forced };
}

export function tarneebScoreRound(bid: number, declarerTricks: number, cfg: TarneebConfig): { declarer: number; defender: number } {
  const made = declarerTricks >= bid;
  return {
    declarer: made ? (bid === 13 ? cfg.bid13Score : declarerTricks) : -bid,
    defender: 13 - declarerTricks,
  };
}

/** Suit the player holds most strongly (length first, then high cards). */
export function tarneebBestSuit(hand: readonly CardId[]): Suit {
  let best: Suit = SUITS[0]!;
  let bestScore = -1;
  for (const suit of SUITS) {
    const ranks = hand.map(parseCard).filter((c) => c.suit === suit).map((c) => c.rank);
    const score = ranks.length * 10 + ranks.reduce((n, r) => n + (r >= 11 ? r - 9 : 0), 0);
    if (score > bestScore) {
      bestScore = score;
      best = suit;
    }
  }
  return best;
}

function deal(state: TarneebState, rng: Rng, events: EngineEvent[]): void {
  const deck = shuffle(buildDeck(), rng);
  const hands: CardId[][] = [[], [], [], []];
  deck.forEach((c, i) => hands[i % SEATS]!.push(c));
  state.hands = hands.map((h) => sortHand(h));
  state.bids = [null, null, null, null];
  state.highBid = null;
  state.trump = null;
  state.tricksWon = [0, 0, 0, 0];
  state.trick = [];
  state.tricks = [];
  state.phase = 'BIDDING';
  state.turn = (state.dealer + 1) % SEATS;
  events.push({ type: 'dealt', round: state.round, dealer: state.dealer, counts: state.hands.map((h) => h.length) });
}

export const tarneebEngine: GameEngine<TarneebConfig, TarneebState, TarneebAction, TarneebView, TarneebLegal> = {
  gameType: 'TARNEEB',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 11, 500);
    checkInt('maxRounds', c.maxRounds, 1, 40);
    checkInt('minBid', c.minBid, 2, 13);
    checkInt('bid13Score', c.bid13Score, 13, 100);
    checkBool('dealerMustBid', c.dealerMustBid);
    checkInt('roundEndDelayMs', c.roundEndDelayMs, 0, 60000);
    checkInt('trickEndDelayMs', c.trickEndDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Tarneeb needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: TarneebState = {
      config,
      players,
      round: 1,
      dealer: rng.int(SEATS),
      phase: 'BIDDING',
      hands: [],
      bids: [],
      highBid: null,
      trump: null,
      tricksWon: [],
      turn: 0,
      trick: [],
      tricks: [],
      teamScores: [0, 0],
      roundResults: [],
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'bid' && typeof raw.value === 'number' && Number.isInteger(raw.value)) return { type: 'bid', value: raw.value };
    if (raw.type === 'pass') return { type: 'pass' };
    if (raw.type === 'trump' && typeof raw.suit === 'string' && (SUITS as readonly string[]).includes(raw.suit)) return { type: 'trump', suit: raw.suit as Suit };
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'play', card: raw.card };
    throw new EngineError('INVALID_ACTION', 'Unknown Tarneeb action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    assertEngine(seat === state.turn && (state.phase === 'BIDDING' || state.phase === 'TRUMP' || state.phase === 'PLAYING'), 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];

    if (action.type === 'bid' || action.type === 'pass') {
      assertEngine(state.phase === 'BIDDING', 'INVALID_PHASE', 'Bidding is closed');
      const range = tarneebBidRange(state, seat);
      if (action.type === 'pass') {
        assertEngine(range.canPass, 'ILLEGAL_MOVE', 'The dealer must bid when everyone else passed');
        state.bids[seat] = 0;
        events.push({ type: 'pass', seat });
      } else {
        assertEngine(action.value >= range.min && action.value <= range.max, 'INVALID_AMOUNT', 'Bid out of range');
        state.bids[seat] = action.value;
        state.highBid = { seat, value: action.value };
        events.push({ type: 'bid', seat, value: action.value });
      }
      if (state.bids.every((b) => b !== null)) {
        const winner = state.highBid!;
        state.phase = 'TRUMP';
        state.turn = winner.seat;
        events.push({ type: 'biddingComplete', declarer: winner.seat, bid: winner.value });
      } else {
        state.turn = (seat + 1) % SEATS;
      }
      return events;
    }

    if (action.type === 'trump') {
      assertEngine(state.phase === 'TRUMP', 'INVALID_PHASE', 'Trump is not being chosen');
      state.trump = action.suit;
      state.phase = 'PLAYING';
      state.turn = seat;
      events.push({ type: 'trumpChosen', seat, suit: action.suit });
      return events;
    }

    assertEngine(state.phase === 'PLAYING', 'INVALID_PHASE', 'Not in play phase');
    const hand = state.hands[seat]!;
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    assertEngine(tarneebLegalCards(hand, state.trick).includes(action.card), 'ILLEGAL_MOVE', 'That card is not allowed now');
    removeCards(hand, [action.card]);
    state.trick.push({ seat, card: action.card });
    events.push({ type: 'cardPlayed', seat, card: action.card });
    if (state.trick.length < SEATS) {
      state.turn = (seat + 1) % SEATS;
      return events;
    }
    const winner = tarneebTrickWinner(state.trick, state.trump!);
    state.tricksWon[winner]!++;
    state.tricks.push({ leader: state.trick[0]!.seat, plays: state.trick, winner });
    events.push({ type: 'trickWon', seat: winner, trickNo: state.tricks.length });
    state.phase = 'TRICK_END';
    state.turn = winner;
    return events;
  },

  getActors(state) {
    return state.phase === 'BIDDING' || state.phase === 'TRUMP' || state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (seat !== state.turn) return { kind: 'none' };
    if (state.phase === 'BIDDING') return { kind: 'bid', ...tarneebBidRange(state, seat) };
    if (state.phase === 'TRUMP') return { kind: 'trump', suits: [...SUITS] };
    if (state.phase === 'PLAYING') return { kind: 'play', cards: tarneebLegalCards(state.hands[seat]!, state.trick) };
    return { kind: 'none' };
  },

  getTimeoutAction(state, seat) {
    if (state.phase === 'BIDDING') {
      const range = tarneebBidRange(state, seat);
      return range.canPass ? { type: 'pass' } : { type: 'bid', value: range.min };
    }
    if (state.phase === 'TRUMP') return { type: 'trump', suit: tarneebBestSuit(state.hands[seat]!) };
    const legal = tarneebLegalCards(state.hands[seat]!, state.trick);
    const trump = state.trump!;
    // Lowest legal card, keeping trumps for last: rarely wastes a winner, never illegal.
    const lowest = [...legal].sort((a, b) => {
      const ca = parseCard(a);
      const cb = parseCard(b);
      return (ca.suit === trump ? 100 : 0) + ca.rank - ((cb.suit === trump ? 100 : 0) + cb.rank);
    })[0]!;
    return { type: 'play', card: lowest };
  },

  getView(state, seat) {
    return {
      gameType: 'TARNEEB',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.round,
      dealer: state.dealer,
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      bids: [...state.bids],
      highBid: state.highBid ? { ...state.highBid } : null,
      declarer: state.phase === 'BIDDING' ? null : (state.highBid?.seat ?? null),
      trump: state.trump,
      tricksWon: [...state.tricksWon],
      trick: state.phase === 'TRICK_END' ? [...(state.tricks.at(-1)?.plays ?? [])] : [...state.trick],
      lastTrick: state.tricks.at(-1) ?? null,
      tricks: state.tricks.map((t) => ({ ...t, plays: [...t.plays] })),
      teamScores: [...state.teamScores],
      roundResults: state.roundResults.map((r) => r.map((x) => ({ ...x }))),
      totals: [0, 1, 2, 3].map((s) => state.teamScores[tarneebTeamOf(s)]!),
    };
  },

  getSystemStep(state) {
    if (state.phase === 'TRICK_END') return { kind: 'collectTrick', delayMs: state.config.trickEndDelayMs };
    if (state.phase === 'ROUND_END') return { kind: 'nextRound', delayMs: state.config.roundEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'TRICK_END') {
      state.trick = [];
      if (state.tricks.length < 13) {
        state.phase = 'PLAYING';
        events.push({ type: 'trickCollected', leader: state.turn });
        return events;
      }
      const cfg = state.config;
      const { seat: declarerSeat, value: bid } = state.highBid!;
      const declarerTeam = tarneebTeamOf(declarerSeat);
      const declarerTricks = state.tricksWon[declarerTeam]! + state.tricksWon[declarerTeam + 2]!;
      const pts = tarneebScoreRound(bid, declarerTricks, cfg);
      const results: TarneebTeamRound[] = [0, 1].map((team) => {
        const tricks = state.tricksWon[team]! + state.tricksWon[team + 2]!;
        return { declarer: team === declarerTeam, bid, tricks, delta: team === declarerTeam ? pts.declarer : pts.defender };
      });
      results.forEach((r, team) => {
        state.teamScores[team]! += r.delta;
      });
      state.roundResults.push(results);
      events.push({ type: 'roundEnd', round: state.round, results, teamScores: [...state.teamScores], tricksWon: [...state.tricksWon] });
      const [a, b] = state.teamScores as [number, number];
      const reached = a >= cfg.targetScore || b >= cfg.targetScore;
      if ((reached && a !== b) || state.round >= cfg.maxRounds) {
        state.phase = 'MATCH_END';
        events.push({ type: 'matchEnd', standings: teamStandings(state) });
      } else {
        state.phase = 'ROUND_END';
      }
      return events;
    }
    if (state.phase === 'ROUND_END') {
      state.round++;
      state.dealer = (state.dealer + 1) % SEATS;
      deal(state, rng, events);
      return events;
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return teamStandings(state);
  },

  getRoundsCompleted: (state) => state.roundResults.length,
};

/** Partners share a place: the leading team is 1st, the other 2nd (both 1st on a tie). */
function teamStandings(state: TarneebState): Standing[] {
  const [a, b] = state.teamScores as [number, number];
  return [0, 1, 2, 3].map((seat) => {
    const team = tarneebTeamOf(seat);
    const mine = team === 0 ? a : b;
    const theirs = team === 0 ? b : a;
    return { seat, score: mine, place: mine >= theirs ? 1 : 2 };
  });
}
